import React, { useState, useEffect } from 'react';
import { useToast } from '../Toast';
import Icon from '../Icon';
import { C, S } from '../../utils/styles';
import TaxSnapshotDetailModal from './TaxSnapshotDetailModal';

export default function TaxSyncHistoryModal({ onClose }) {
  const addToast = useToast();
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    triggeredBy: '',
    search: ''
  });

  useEffect(() => {
    fetchHistory();
  }, [filters]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('healthcues_token');
      const backendUrl = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
      
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.triggeredBy) params.append('triggered_by', filters.triggeredBy);
      
      const response = await fetch(`${backendUrl}/api/admin/tax-api/history?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      } else {
        addToast('Failed to fetch history', 'error');
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
      addToast('Failed to fetch sync history', 'error');
    } finally {
      setLoading(false);
    }
  };

  const statusBadge = (status) => {
    const badges = {
      applied: { bg: 'rgb(220, 252, 231)', color: 'rgb(22, 163, 74)', text: 'Applied' },
      pending_review: { bg: 'rgb(254, 243, 199)', color: 'rgb(217, 119, 6)', text: 'Pending Review' },
      ignored: { bg: 'rgb(241, 245, 249)', color: 'rgb(100, 116, 139)', text: 'Ignored' },
      failed: { bg: 'rgb(254, 226, 226)', color: 'rgb(220, 38, 38)', text: 'Failed' }
    };
    const badge = badges[status] || badges.pending_review;

    return (
      <span style={{
        padding: '4px 10px',
        borderRadius: 6,
        background: badge.bg,
        color: badge.color,
        fontSize: 12,
        fontWeight: 600
      }}>
        {badge.text}
      </span>
    );
  };

  const filteredHistory = history.filter(item => {
    if (!filters.search) return true;
    const query = filters.search.toLowerCase();
    return item.id.toLowerCase().includes(query) ||
           item.effectiveDate.toLowerCase().includes(query) ||
           (item.actorName && item.actorName.toLowerCase().includes(query));
  });

  return (
    <>
      <div
        data-testid="tax-sync-history-modal"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 24
        }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div style={{
          background: 'white',
          borderRadius: 14,
          boxShadow: '0 10px 30px rgba(2, 6, 23, 0.15)',
          maxWidth: 1200,
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header */}
          <div style={{
            padding: '20px 24px',
            borderBottom: `1px solid ${C.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div>
              <h2 style={{ ...S.heading, margin: 0 }}>Tax Sync History</h2>
              <p style={{ fontSize: 13, color: C.textMid, marginTop: 4 }}>View all tax sync operations and audit logs</p>
            </div>
            <button
              onClick={onClose}
              style={{
                padding: 8,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                color: C.textMid,
                borderRadius: 8
              }}
            >
              <Icon name="close-outline" style={{ fontSize: 24 }} />
            </button>
          </div>

          {/* Filters */}
          <div style={{
            padding: '16px 24px',
            borderBottom: `1px solid ${C.border}`,
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 12
          }}>
            <div>
              <label htmlFor="status-filter" style={{ ...S.label, fontSize: 12 }}>Status</label>
              <select
                id="status-filter"
                name="statusFilter"
                data-testid="tax-sync-history-status-filter"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                style={{
                  ...S.input,
                  fontSize: 13
                }}
              >
                <option value="">All Statuses</option>
                <option value="applied">Applied</option>
                <option value="pending_review">Pending Review</option>
                <option value="ignored">Ignored</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            <div>
              <label htmlFor="trigger-filter" style={{ ...S.label, fontSize: 12 }}>Triggered By</label>
              <select
                id="trigger-filter"
                name="triggerFilter"
                data-testid="tax-sync-history-trigger-filter"
                value={filters.triggeredBy}
                onChange={(e) => setFilters({ ...filters, triggeredBy: e.target.value })}
                style={{
                  ...S.input,
                  fontSize: 13
                }}
              >
                <option value="">All Types</option>
                <option value="manual">Manual</option>
                <option value="auto">Automated</option>
                <option value="system">System</option>
              </select>
            </div>

            <div>
              <label htmlFor="search-filter" style={{ ...S.label, fontSize: 12 }}>Search</label>
              <input
                id="search-filter"
                name="searchFilter"
                data-testid="tax-sync-history-search-input"
                type="text"
                placeholder="Snapshot ID, date, user..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                style={{
                  ...S.input,
                  fontSize: 13
                }}
              />
            </div>
          </div>

          {/* Body */}
          <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Icon name="sync-outline" style={{ fontSize: 32, color: C.accent, animation: 'spin 1s linear infinite' }} />
                <p style={{ marginTop: 12, color: C.textMid }}>Loading history...</p>
              </div>
            ) : filteredHistory.length > 0 ? (
              <div style={{
                border: `1px solid ${C.border}`,
                borderRadius: 10,
                overflow: 'hidden'
              }}>
                <table data-testid="tax-sync-history-table" style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{
                      background: 'rgb(248, 250, 252)',
                      borderBottom: `1px solid ${C.border}`
                    }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, textTransform: 'uppercase' }}>Timestamp</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, textTransform: 'uppercase' }}>Status</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, textTransform: 'uppercase' }}>Triggered By</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, textTransform: 'uppercase' }}>Changes</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, textTransform: 'uppercase' }}>Duration</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, textTransform: 'uppercase' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredHistory.map((item, idx) => (
                      <tr
                        key={item.id}
                        style={{
                          borderBottom: idx < filteredHistory.length - 1 ? `1px solid ${C.border}` : 'none',
                          cursor: 'pointer'
                        }}
                        onClick={() => setSelectedSnapshot(item.id)}
                      >
                        <td style={{ padding: '12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>
                          {new Date(item.timestamp).toLocaleString()}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {statusBadge(item.status)}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>
                            {item.triggeredBy === 'manual' ? 'Manual' : item.triggeredBy === 'auto' ? 'Automated' : 'System'}
                          </div>
                          {item.actorName && (
                            <div style={{ fontSize: 12, color: C.textMid }}>{item.actorName}</div>
                          )}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ fontSize: 12, color: C.textMid }}>
                            +{item.summary.added || 0} ↻{item.summary.updated || 0} −{item.summary.removed || 0}
                          </div>
                        </td>
                        <td style={{ padding: '12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: C.textMid }}>
                          {item.duration ? `${item.duration}ms` : '—'}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedSnapshot(item.id);
                            }}
                            style={{
                              padding: '4px 12px',
                              borderRadius: 6,
                              border: `1px solid ${C.border}`,
                              background: 'white',
                              color: C.accent,
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div data-testid="tax-sync-history-empty-state" style={{
                padding: 40,
                textAlign: 'center',
                border: `1px solid ${C.border}`,
                borderRadius: 10,
                background: 'rgb(248, 250, 252)'
              }}>
                <Icon name="time-outline" style={{ fontSize: 48, color: C.textMid }} />
                <h3 style={{ marginTop: 12, fontSize: 16, color: C.text }}>No sync history yet</h3>
                <p style={{ fontSize: 13, color: C.textMid, marginTop: 4 }}>
                  Run a manual sync to create an audit trail.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedSnapshot && (
        <TaxSnapshotDetailModal
          snapshotId={selectedSnapshot}
          onClose={() => setSelectedSnapshot(null)}
        />
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}
